import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage } from '@hypevault/shared-types';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { gameSounds } from '../lib/gameSounds';

/**
 * HypePets Fullscreen — 2026-06-20.
 *
 * Cinematic 1920×1080 takeover widget. Solid background, big bold
 * display type, three zones at a glance:
 *   - top banner: HYPEPETS lockup + live-status pill
 *   - center stage (left ~60%): current wild encounter, or a
 *     pulsing "waiting for the next wild" panel
 *   - leaderboard (right ~40%): top-8 viewers, rotating through the
 *     same five metrics the standalone leaderboard widget cycles
 *     (levels / pets / shinies / perfects / crystals)
 *   - bottom strip: how-to-play command reference
 *
 * Data sources (no new endpoints):
 *   - gameStateEvent (already piped through WidgetApp) for the wild
 *   - GET /api/streamers/:id/hypepets/leaderboard?limit=20 (existing)
 *
 * Routed via widgetType === 'hypepets_fullscreen' in WidgetApp.
 */

const DISPLAY_FONT = "'Bebas Neue', 'Oswald', 'Impact', sans-serif";

interface WildParticipant {
  viewer: string;
  damageDealt: number;
  catchAttempted?: boolean;
  caught?: boolean;
  /** Snapshot of the attacker's pet at the time they last swung.
   *  Written by gameRunner.ts when the participant row is created /
   *  updated. Optional because legacy snapshots from before Pass 28as
   *  didn't include them. */
  currentHp?: number;
  maxHp?: number;
  isFainted?: boolean;
}

interface ActiveWild {
  id: string;
  rarity: 'uncommon' | 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';
  species: {
    id: string;
    name: string;
    emoji: string;
    imageUrl?: string | null;
    element?: string | null;
    secondaryElement?: string | null;
    theme?: string;
  };
  level: number;
  maxHp: number;
  currentHp: number;
  status: 'attacking' | 'catch_phase' | 'concluded';
  spawnedAt: number;
  escapeAt: number;
  catchPhaseEndsAt: number | null;
  participants: WildParticipant[];
}

interface HypeEggState {
  activeWild?: ActiveWild | null;
}

interface GameStateEvent {
  gameType: string;
  state: unknown;
  config: unknown;
  isActive: boolean;
}

interface LeaderboardEntry {
  platformUsername: string;
  platform: 'twitch' | 'youtube' | 'kick';
  pets: number;
  totalLevels: number;
  shinies: number;
  perfects: number;
  crystalsInvested: number;
}

type MetricId = 'levels' | 'pets' | 'shinies' | 'perfects' | 'crystals';
interface MetricDef {
  id: MetricId;
  title: string;
  subtitle: string;
  icon: string;
  unit: string;
  accent: string;
  read: (e: LeaderboardEntry) => number;
}
const METRICS: Record<MetricId, MetricDef> = {
  levels: {
    id: 'levels',
    title: 'Top Trainers',
    subtitle: 'Total Levels',
    icon: '🏆',
    unit: 'Lv',
    accent: '#a855f7',
    read: (e) => e.totalLevels,
  },
  pets: {
    id: 'pets',
    title: 'Master Collectors',
    subtitle: 'Pets Owned',
    icon: '🐾',
    unit: 'pets',
    accent: '#22d3ee',
    read: (e) => e.pets,
  },
  shinies: {
    id: 'shinies',
    title: 'Shiny Hunters',
    subtitle: 'Shiny Pets',
    icon: '✨',
    unit: '✨',
    accent: '#fbbf24',
    read: (e) => e.shinies,
  },
  perfects: {
    id: 'perfects',
    title: 'Perfectionists',
    subtitle: 'Perfect Pets',
    icon: '💯',
    unit: 'perfect',
    accent: '#34d399',
    read: (e) => e.perfects,
  },
  crystals: {
    id: 'crystals',
    title: 'Evolution Tycoons',
    subtitle: 'Crystals Invested',
    icon: '💎',
    unit: 'crystals',
    accent: '#f472b6',
    read: (e) => e.crystalsInvested,
  },
};
const ALL_METRICS: MetricId[] = ['levels', 'pets', 'shinies', 'perfects', 'crystals'];

const RARITY_COLOR: Record<ActiveWild['rarity'], string> = {
  common: '#a1a1aa',
  uncommon: '#34d399',
  rare: '#38bdf8',
  epic: '#a855f7',
  legendary: '#facc15',
  mythic: '#f87171',
};

const PLATFORM_GLYPH: Record<'twitch' | 'youtube' | 'kick', string> = {
  twitch: '🟣',
  youtube: '▶️',
  kick: '🟢',
};

interface Fields {
  /** Override accent color of the chrome (default purple). */
  accentColor?: string;
  /** Subset of leaderboard metrics to cycle. Default all 5. */
  metrics?: string[];
  /** Per-metric dwell time. Default 8000ms. */
  rotateMs?: number;
  /** Top-N leaderboard rows visible. Default 5. */
  limit?: number;
  /** Override the header title. Default 'HYPEPETS'. */
  title?: string;
  /** Override the bottom commands tagline. */
  commandsTagline?: string;
  /** When true (default), the right column reserves a 16:9 box above
   *  the leaderboard for the streamer's webcam source. The widget
   *  itself doesn't fill that box — it shows a styled placeholder
   *  the streamer's OBS camera source layers on top of. Set false
   *  to give the leaderboard the full right-column height. */
  showCameraPlaceholder?: boolean;
  /** Optional label inside the camera placeholder. Default "STREAMER
   *  CAM" — change to a channel name, an alert, anything. */
  cameraPlaceholderLabel?: string;
  /** 2026-06-20 — between-fights "Who's That Pet?" mini-game on the
   *  wild stage. Default true. Streamers who want a pure "waiting"
   *  screen between encounters can disable. */
  showMinigame?: boolean;
  /** 2026-06-21 — top-N attackers visible in the wild-stage right
   *  column. Default 10. Clamps 3–15 (each row is a 2-line ~46px
   *  chip; 15 is the most the right column can fit without pushing
   *  into the HP bar / phase chip). */
  wildAttackersLimit?: number;
}

export function HypePetsFullscreenWidget({
  streamerId,
  gameStateEvent,
  latestChatMessage,
  fields,
}: {
  streamerId: string;
  gameStateEvent: GameStateEvent | null;
  /** Latest chat-message socket push — drives Who's That Pet? guess
   *  matching. Optional so legacy consumers don't break. */
  latestChatMessage?: ChatMessage | null;
  fields: Fields;
}) {
  const accent =
    typeof fields.accentColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(fields.accentColor)
      ? fields.accentColor
      : '#a855f7';
  const secondary = '#22d3ee';
  const title = (typeof fields.title === 'string' && fields.title.trim()) || 'HYPEPETS';
  const commandsTagline =
    (typeof fields.commandsTagline === 'string' && fields.commandsTagline.trim()) ||
    'Type these in chat to play';
  // 2026-06-20 — `limit` now caps the rows IN the scrolling list (the
  // panel scrolls up to reveal the rest). Visible window is fixed by
  // the right-column height. Pre-scroll behavior was hard-cap-5, so
  // bump default to 20 to actually show off the scroll.
  const leaderLimit = Math.max(3, Math.min(50, Number(fields.limit) || 20));
  const rotateMs = Math.max(2000, Math.min(60_000, Number(fields.rotateMs) || 8000));
  const showCameraPlaceholder = fields.showCameraPlaceholder !== false;
  const cameraLabel =
    (typeof fields.cameraPlaceholderLabel === 'string' && fields.cameraPlaceholderLabel.trim()) ||
    'STREAMER CAM';
  const showMinigame = fields.showMinigame !== false;
  const wildAttackersLimit = Math.max(3, Math.min(15, Number(fields.wildAttackersLimit) || 10));

  const enabledMetrics: MetricId[] = useMemo(() => {
    if (!Array.isArray(fields.metrics) || fields.metrics.length === 0) return ALL_METRICS;
    const picked = fields.metrics.filter((m): m is MetricId => ALL_METRICS.includes(m as MetricId));
    return picked.length > 0 ? picked : ALL_METRICS;
  }, [fields.metrics]);

  // ── Sound manager ────────────────────────────────────────────────
  // Configure once on mount + unlock the AudioContext on first user
  // interaction (OBS CEF allows autoplay; direct-browser testers need
  // a click first). Mirrors HypeEggWidget so the two stay in sync.
  useEffect(() => {
    gameSounds.configure(DEFAULT_GAME_SOUND_CONFIG);
    function unlock() {
      void gameSounds.unlock();
    }
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // ── Wild-encounter snapshot ──────────────────────────────────────
  // Bootstrap from /api/hypeegg/snapshot on mount so a widget that
  // loads mid-session immediately knows HypePets is active (no
  // "OFFLINE" flash while we wait for the next socket push). Same
  // pattern HypeEggWidget uses. Then merge live socket pushes.
  // 2026-06-21 — bootstrap also captures widgetFields.sounds so the
  // per-streamer sound URL overrides land before any wild fires.
  const [wild, setWild] = useState<ActiveWild | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [soundUrls, setSoundUrls] = useState<Record<string, string> | null>(null);
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (bootstrappedRef.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    bootstrappedRef.current = true;
    const ac = new AbortController();
    fetch(`/api/hypeegg/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (
          json: {
            state: HypeEggState | null;
            isActive?: boolean;
            widgetFields?: { sounds?: Record<string, string> } | null;
          } | null,
        ) => {
          if (!json) return;
          setIsActive(json.isActive ?? false);
          setWild(json.state?.activeWild ?? null);
          if (json.widgetFields?.sounds) setSoundUrls(json.widgetFields.sounds);
        },
      )
      .catch(() => undefined);
    return () => ac.abort();
  }, []);
  useEffect(() => {
    if (!gameStateEvent || gameStateEvent.gameType !== 'hypeegg') return;
    setIsActive(gameStateEvent.isActive);
    const state = gameStateEvent.state as HypeEggState | null;
    setWild(state?.activeWild ?? null);
  }, [gameStateEvent]);

  // 2026-06-21 — apply per-streamer sound URL overrides. Re-runs only
  // when soundUrls changes (i.e. when bootstrap or a config refresh
  // delivers a new map). Empty/missing keys leave the default URL
  // registered at module load.
  useEffect(() => {
    if (!soundUrls) return;
    const map: Record<string, string> = soundUrls;
    const keys = [
      'wild_spawn',
      'wild_attack',
      'wild_miss',
      'wild_heal',
      'wild_catch_success',
      'wild_catch_fail',
      'correct',
      'reveal',
    ] as const;
    for (const k of keys) {
      const url = map[k];
      if (url) gameSounds.registerPackAsset('default', k as never, url);
    }
  }, [soundUrls]);

  // 2026-06-21 — state-edge sound triggers tied to wild transitions.
  // Mirrors HypeEggWidget exactly so the same audio plays whether a
  // streamer runs the octagon, the fullscreen widget, or both. Refs
  // hold the previous values so playback only fires on the rising
  // edge, not every re-render.
  const prevWildIdRef = useRef<string | null>(null);
  const prevWildHpRef = useRef<number | null>(null);
  const prevCaughtRef = useRef<Set<string>>(new Set());
  const prevAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!wild) {
      prevWildIdRef.current = null;
      prevWildHpRef.current = null;
      prevCaughtRef.current = new Set();
      prevAttemptedRef.current = new Set();
      return;
    }
    if (wild.id !== prevWildIdRef.current) {
      void gameSounds.play('wild_spawn');
      prevCaughtRef.current = new Set();
      prevAttemptedRef.current = new Set();
    } else if (prevWildHpRef.current != null && wild.currentHp < prevWildHpRef.current) {
      void gameSounds.play('wild_attack');
    }
    prevWildIdRef.current = wild.id;
    prevWildHpRef.current = wild.currentHp;
    for (const p of wild.participants ?? []) {
      const key = p.viewer;
      if (p.caught && !prevCaughtRef.current.has(key)) {
        void gameSounds.play('wild_catch_success');
        prevCaughtRef.current.add(key);
      } else if (p.catchAttempted && !p.caught && !prevAttemptedRef.current.has(key)) {
        void gameSounds.play('wild_catch_fail');
        prevAttemptedRef.current.add(key);
      }
    }
  }, [wild]);

  // ── Live ticker for the HP bar / escape clock ─────────────────────
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  // ── Leaderboard fetch (60s) ──────────────────────────────────────
  const [rows, setRows] = useState<LeaderboardEntry[] | null>(null);
  useEffect(() => {
    if (!streamerId) return;
    let cancelled = false;
    const load = () => {
      fetch(`/api/streamers/${streamerId}/hypepets/leaderboard?limit=50`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((json: { rows?: LeaderboardEntry[] }) => {
          if (cancelled) return;
          setRows(json.rows ?? []);
        })
        .catch((err) => {
          if (!cancelled) console.error('[hypepets-fullscreen] leaderboard fetch failed', err);
        });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [streamerId]);

  // ── Metric rotation ─────────────────────────────────────────────
  const [metricIdx, setMetricIdx] = useState(0);
  useEffect(() => {
    if (enabledMetrics.length <= 1) return;
    const id = setInterval(() => {
      setMetricIdx((i) => (i + 1) % enabledMetrics.length);
    }, rotateMs);
    return () => clearInterval(id);
  }, [enabledMetrics, rotateMs]);
  const metricId = enabledMetrics[metricIdx % enabledMetrics.length] ?? enabledMetrics[0]!;
  const metric = METRICS[metricId];

  const rankedRows = useMemo(() => {
    if (!rows) return null;
    const filtered = rows.filter((r) => metric.read(r) > 0);
    filtered.sort((a, b) => metric.read(b) - metric.read(a));
    return filtered.slice(0, leaderLimit);
  }, [rows, metric, leaderLimit]);

  // ── Wild status copy ────────────────────────────────────────────
  const wildStatusLabel = !isActive
    ? 'OFFLINE'
    : wild
      ? wild.status === 'attacking'
        ? 'WILD INCOMING'
        : wild.status === 'catch_phase'
          ? 'CATCH PHASE OPEN'
          : 'RESOLVING'
      : 'HUNTING';
  const wildStatusColor = !isActive
    ? '#6b7280'
    : wild
      ? wild.status === 'catch_phase'
        ? '#22d3ee'
        : RARITY_COLOR[wild.rarity]
      : accent;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', 'Poppins', system-ui, sans-serif",
        color: 'white',
        background: '#05030f',
      }}
    >
      {/* Animated gradient mesh */}
      <div
        style={{
          position: 'absolute',
          inset: -200,
          background: `radial-gradient(circle at 20% 30%, ${accent}66, transparent 55%)`,
          filter: 'blur(100px)',
          animation: 'hpfs-drift-a 18s ease-in-out infinite',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: -200,
          background: `radial-gradient(circle at 80% 70%, ${secondary}55, transparent 55%)`,
          filter: 'blur(100px)',
          animation: 'hpfs-drift-b 22s ease-in-out infinite',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)',
          pointerEvents: 'none',
        }}
      />
      {/* Scanlines */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 3px)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />
      {/* Left vertical accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 8,
          background: `linear-gradient(180deg, transparent 0%, ${accent} 25%, ${secondary} 75%, transparent 100%)`,
          boxShadow: `0 0 32px ${accent}77`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: 8,
          background: `linear-gradient(180deg, transparent 0%, ${secondary} 25%, ${accent} 75%, transparent 100%)`,
          boxShadow: `0 0 32px ${secondary}77`,
        }}
      />

      {/* ─── TOP BANNER ─────────────────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 60,
          right: 60,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: 6,
              color: 'rgba(255,255,255,0.55)',
              textTransform: 'uppercase',
            }}
          >
            Stream Game
          </span>
          <span
            style={{
              fontFamily: DISPLAY_FONT,
              fontSize: 120,
              lineHeight: 0.85,
              letterSpacing: -2,
              background: `linear-gradient(135deg, ${accent}, ${secondary})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: `0 0 60px ${accent}55`,
            }}
          >
            {title}
          </span>
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 22px',
            borderRadius: 4,
            background: 'rgba(0,0,0,0.5)',
            border: `1px solid ${wildStatusColor}77`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: 5,
            color: 'white',
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              background: wildStatusColor,
              boxShadow: `0 0 16px ${wildStatusColor}, 0 0 32px ${wildStatusColor}`,
              animation: 'hpfs-blink 1.4s ease-in-out infinite',
            }}
          />
          {wildStatusLabel}
        </div>
      </div>

      {/* ─── BODY (LEFT: WILD · RIGHT: LEADERBOARD) ─────────────── */}
      <div
        style={{
          position: 'absolute',
          top: 220,
          bottom: 160,
          left: 60,
          right: 60,
          display: 'grid',
          gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)',
          gap: 40,
        }}
      >
        <WildStage
          wild={wild}
          isActive={isActive}
          accent={accent}
          secondary={secondary}
          streamerId={streamerId}
          latestChatMessage={latestChatMessage ?? null}
          showMinigame={showMinigame}
          attackersLimit={wildAttackersLimit}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minHeight: 0,
          }}
        >
          {showCameraPlaceholder && (
            <CameraPlaceholder label={cameraLabel} accent={accent} secondary={secondary} />
          )}
          <div style={{ flex: 1, minHeight: 0 }}>
            <LeaderboardPanel
              metric={metric}
              rows={rankedRows}
              accent={metric.accent}
              rotating={enabledMetrics.length > 1}
            />
          </div>
        </div>
      </div>

      {/* ─── BOTTOM COMMANDS STRIP ──────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          left: 60,
          right: 60,
          bottom: 36,
          padding: '14px 24px',
          borderRadius: 8,
          background: 'rgba(0,0,0,0.55)',
          border: `1px solid ${accent}55`,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 4,
            color: `${accent}cc`,
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          {commandsTagline}
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1, minWidth: 0 }}>
          {COMMANDS.map((c) => (
            <CommandChip key={c.cmd} cmd={c.cmd} desc={c.desc} accent={c.accent ?? accent} />
          ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Oswald:wght@700;800&display=swap');
        @keyframes hpfs-drift-a {
          0%, 100% { transform: translate(0,0) scale(1); }
          50%      { transform: translate(160px,-80px) scale(1.15); }
        }
        @keyframes hpfs-drift-b {
          0%, 100% { transform: translate(0,0) scale(1); }
          50%      { transform: translate(-180px,120px) scale(1.1); }
        }
        @keyframes hpfs-blink {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.3; }
        }
        @keyframes hpfs-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.04); opacity: 0.85; }
        }
        @keyframes hpfs-hp-shimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes hpfs-catch-pop {
          /* Quick zoom-bounce on the ✅/❌ badge so the moment the
             catch result writes, the row visibly punctuates instead
             of just changing color. */
          0%   { transform: scale(0.4); opacity: 0; }
          60%  { transform: scale(1.3); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes hpfs-leaderboard-scroll {
          /* Two copies of the row list stacked back-to-back; translating
             -50% lands exactly on the second copy's first row, which
             visually matches the first copy's first row — invisible
             loop seam. Linear timing keeps the marquee at a constant
             pace; ease-in-out would lurch at the seam. */
          0%   { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
      `}</style>
    </div>
  );
}

const COMMANDS: Array<{ cmd: string; desc: string; accent?: string }> = [
  { cmd: '!hypepets', desc: 'Claim a starter pet (first time)' },
  { cmd: '!attack', desc: 'Damage the wild · 1/2 = damage move · 3 = support' },
  { cmd: '!catch', desc: 'Capture once the wild is downed' },
  { cmd: '!forage', desc: 'Send a pet hunting for items' },
  { cmd: '!heal', desc: 'Auto-pick the best heal / revive item' },
  { cmd: '!pethelp', desc: 'Full command list' },
];

function CommandChip({ cmd, desc, accent }: { cmd: string; desc: string; accent: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 4,
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${accent}55`,
        fontSize: 13,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontWeight: 800,
          color: accent,
          flexShrink: 0,
        }}
      >
        {cmd}
      </span>
      <span
        style={{
          color: 'rgba(255,255,255,0.7)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {desc}
      </span>
    </div>
  );
}

function WildStage({
  wild,
  isActive,
  accent,
  secondary,
  streamerId,
  latestChatMessage,
  showMinigame,
  attackersLimit,
}: {
  wild: ActiveWild | null;
  isActive: boolean;
  accent: string;
  secondary: string;
  streamerId: string;
  latestChatMessage: ChatMessage | null;
  showMinigame: boolean;
  attackersLimit: number;
}) {
  void streamerId;
  if (!isActive) {
    return (
      <IdlePanel
        title="HypePets is OFFLINE"
        subtitle="Streamer hasn't started a session"
        accent={accent}
      />
    );
  }
  if (!wild) {
    // 2026-06-20 — Who's That Pet? mini-game fills the between-fights
    // window so chat has something to do while waiting. Streamers who
    // want a quiet "waiting" panel can flip showMinigame off.
    if (showMinigame) {
      return (
        <WhosThatPetPanel
          accent={accent}
          secondary={secondary}
          latestChatMessage={latestChatMessage}
        />
      );
    }
    return (
      <IdlePanel
        title="Hunting for the next wild…"
        subtitle="A new encounter could spawn any minute. Stay sharp."
        accent={accent}
        pulse
      />
    );
  }
  const hpPct = Math.max(0, Math.min(1, wild.currentHp / Math.max(1, wild.maxHp)));
  const phaseLabel =
    wild.status === 'catch_phase'
      ? 'CATCH PHASE'
      : wild.status === 'concluded'
        ? 'RESOLVING'
        : 'ATTACK PHASE';
  const phaseColor =
    wild.status === 'catch_phase'
      ? '#22d3ee'
      : wild.status === 'concluded'
        ? '#6b7280'
        : RARITY_COLOR[wild.rarity];
  const endsAt =
    wild.status === 'catch_phase' && wild.catchPhaseEndsAt ? wild.catchPhaseEndsAt : wild.escapeAt;
  const remainingSec = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
  // 2026-06-20 — sort priority: catch outcomes float to the top so
  // ✅/❌ trap results always surface even when the catcher wasn't a
  // top damage dealer. Within each bucket, sort by damage desc.
  // Order: caught → missed → still-pending attackers.
  // 2026-06-21 — list is no longer sliced; the attacker scroller below
  // marquees through the full sorted list so every swinger gets
  // visible time on stream. attackersLimit is preserved as the
  // visible-window threshold (lists ≤ limit render static, no scroll).
  const catchPriority = (p: WildParticipant) => (p.caught ? 0 : p.catchAttempted ? 1 : 2);
  const sortedAttackers = [...wild.participants].sort((a, b) => {
    const pa = catchPriority(a);
    const pb = catchPriority(b);
    if (pa !== pb) return pa - pb;
    return b.damageDealt - a.damageDealt;
  });

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        borderRadius: 12,
        background: 'rgba(0,0,0,0.45)',
        border: `1px solid ${RARITY_COLOR[wild.rarity]}55`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        padding: 32,
        overflow: 'hidden',
        boxShadow: `0 24px 80px -20px ${RARITY_COLOR[wild.rarity]}33, inset 0 0 80px ${RARITY_COLOR[wild.rarity]}11`,
        display: 'grid',
        gridTemplateColumns: '320px minmax(0,1fr)',
        gap: 36,
      }}
    >
      {/* Species art column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 320,
            height: 320,
            borderRadius: 12,
            border: `2px solid ${RARITY_COLOR[wild.rarity]}`,
            background: 'rgba(0,0,0,0.5)',
            overflow: 'hidden',
            display: 'grid',
            placeItems: 'center',
            boxShadow: `0 0 60px ${RARITY_COLOR[wild.rarity]}55`,
          }}
        >
          {wild.species.imageUrl ? (
            <img
              src={wild.species.imageUrl}
              alt={wild.species.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ fontSize: 200, lineHeight: 1 }}>{wild.species.emoji}</span>
          )}
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            borderRadius: 4,
            background: `${RARITY_COLOR[wild.rarity]}22`,
            color: RARITY_COLOR[wild.rarity],
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 4,
            textTransform: 'uppercase',
          }}
        >
          {wild.rarity}
        </span>
      </div>

      {/* Details column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '4px 12px',
              borderRadius: 4,
              background: `${phaseColor}22`,
              color: phaseColor,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 3,
              textTransform: 'uppercase',
            }}
          >
            {phaseLabel}
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 14,
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            {remainingSec}s left
          </span>
        </div>
        <div
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: 92,
            lineHeight: 0.9,
            letterSpacing: -1,
            color: 'white',
            textShadow: `0 0 60px ${RARITY_COLOR[wild.rarity]}55`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {wild.species.name.toUpperCase()}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
            Lv. {wild.level}
          </span>
          {wild.species.element && (
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 2,
                color: 'rgba(255,255,255,0.55)',
                textTransform: 'uppercase',
              }}
            >
              {wild.species.element}
              {wild.species.secondaryElement ? ` / ${wild.species.secondaryElement}` : ''}
            </span>
          )}
          {wild.species.theme && (
            <span
              style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 999,
                background: `${secondary}22`,
                color: secondary,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
              }}
            >
              {wild.species.theme}
            </span>
          )}
        </div>
        {/* HP bar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 3,
              color: 'rgba(255,255,255,0.55)',
              textTransform: 'uppercase',
            }}
          >
            <span>HP</span>
            <span>
              {wild.currentHp} / {wild.maxHp}
            </span>
          </div>
          <div
            style={{
              height: 18,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.05)',
              overflow: 'hidden',
              border: `1px solid ${RARITY_COLOR[wild.rarity]}44`,
            }}
          >
            <div
              style={{
                width: `${hpPct * 100}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${RARITY_COLOR[wild.rarity]}, ${secondary}, ${RARITY_COLOR[wild.rarity]})`,
                backgroundSize: '200% 100%',
                animation: 'hpfs-hp-shimmer 3s linear infinite',
                transition: 'width 400ms ease',
              }}
            />
          </div>
        </div>

        {/* Attackers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 4,
                color: 'rgba(255,255,255,0.5)',
                textTransform: 'uppercase',
              }}
            >
              Top Attackers ({wild.participants.length})
            </span>
            {/* 2026-06-20 — catch tally pills. Visible whenever at least
                one !catch attempt has fired this encounter — keeps the
                trap-result count front-and-center even after the row
                scrolls past the visible top-5. */}
            {(() => {
              const caughtN = wild.participants.filter((p) => p.caught).length;
              const missedN = wild.participants.filter((p) => p.catchAttempted && !p.caught).length;
              if (caughtN === 0 && missedN === 0) return null;
              return (
                <>
                  {caughtN > 0 && (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'rgba(34,197,94,0.18)',
                        border: '1px solid rgba(34,197,94,0.55)',
                        color: '#86efac',
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 1.6,
                        textTransform: 'uppercase',
                      }}
                    >
                      ✅ {caughtN} caught
                    </span>
                  )}
                  {missedN > 0 && (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'rgba(239,68,68,0.18)',
                        border: '1px solid rgba(239,68,68,0.55)',
                        color: '#fca5a5',
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 1.6,
                        textTransform: 'uppercase',
                      }}
                    >
                      ❌ {missedN} missed
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          {sortedAttackers.length === 0 ? (
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
              No one's swung yet — type !attack to engage
            </span>
          ) : (
            <ScrollingAttackerList
              attackers={sortedAttackers}
              visibleThreshold={attackersLimit}
              accent={accent}
              secondary={secondary}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** 2026-06-20 — 16:9 placeholder above the leaderboard. The widget
 *  itself never renders a video feed — this is just a visually-
 *  intentional empty zone the streamer layers their OBS camera
 *  source on top of. Looks fine standalone too (matches the rest of
 *  the chrome) so a misconfigured cam doesn't leave an ugly hole. */
function CameraPlaceholder({
  label,
  accent,
  secondary,
}: {
  label: string;
  accent: string;
  secondary: string;
}) {
  return (
    <div
      style={{
        position: 'relative',
        // 16:9 box; let the parent flex give us width, height tracks
        // automatically. Browsers without aspect-ratio support fall
        // back to whatever min-height the flex parent enforces.
        aspectRatio: '16 / 9',
        borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%)',
        border: `2px dashed ${accent}66`,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 24px 80px -20px ${accent}33, inset 0 0 80px ${accent}11`,
      }}
    >
      {/* Decorative corner brackets so the box reads as a deliberate
          frame, not a placeholder by accident. */}
      {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => {
        const isTop = corner.startsWith('t');
        const isLeft = corner.endsWith('l');
        return (
          <span
            key={corner}
            style={{
              position: 'absolute',
              top: isTop ? 14 : undefined,
              bottom: isTop ? undefined : 14,
              left: isLeft ? 14 : undefined,
              right: isLeft ? undefined : 14,
              width: 28,
              height: 28,
              borderTop: isTop ? `3px solid ${accent}` : 'none',
              borderBottom: isTop ? 'none' : `3px solid ${accent}`,
              borderLeft: isLeft ? `3px solid ${accent}` : 'none',
              borderRight: isLeft ? 'none' : `3px solid ${accent}`,
            }}
          />
        );
      })}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          textAlign: 'center',
        }}
      >
        <span style={{ fontSize: 64, lineHeight: 1, filter: `drop-shadow(0 0 16px ${accent}66)` }}>
          🎥
        </span>
        <span
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: 36,
            lineHeight: 0.9,
            letterSpacing: 1,
            color: 'white',
            textShadow: `0 0 24px ${accent}99`,
          }}
        >
          {label.toUpperCase()}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            color: `${secondary}cc`,
            textTransform: 'uppercase',
          }}
        >
          Layer your OBS camera here
        </span>
      </div>
    </div>
  );
}

/** 2026-06-21 — slow vertical marquee for the attacker list. Same
 *  pattern the ScrollingLeaderboard uses: render the list twice
 *  back-to-back, translateY 0 → -50% linearly so the seam between
 *  copies is invisible. Pauses (renders static + no doubled list)
 *  when the list is short enough to fit the visible window. */
function ScrollingAttackerList({
  attackers,
  visibleThreshold,
  accent,
  secondary,
}: {
  attackers: WildParticipant[];
  visibleThreshold: number;
  accent: string;
  secondary: string;
}) {
  const SECONDS_PER_ROW = 4; // dwell per row when scrolling
  const shouldScroll = attackers.length > visibleThreshold;
  const duration = Math.max(20, Math.round(attackers.length * SECONDS_PER_ROW));

  const renderRow = (p: WildParticipant, i: number, keyPrefix: string) => (
    <AttackerRow
      key={`${keyPrefix}:${p.viewer}`}
      rank={i + 1}
      participant={p}
      isLeader={i === 0}
      accent={accent}
      secondary={secondary}
    />
  );

  if (!shouldScroll) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {attackers.map((p, i) => renderRow(p, i, 'a'))}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        // Cap height so the marquee has a fixed visible window rather
        // than growing to fill the parent — keeps the HP bar + phase
        // chip above unaffected by attacker count. ~50px per chip ×
        // visibleThreshold + a little padding for the gap.
        height: visibleThreshold * 54,
        maxHeight: visibleThreshold * 54,
        overflow: 'hidden',
        maskImage:
          'linear-gradient(180deg, transparent 0, black 20px, black calc(100% - 20px), transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(180deg, transparent 0, black 20px, black calc(100% - 20px), transparent 100%)',
      }}
    >
      <div
        // Keyed on the participant count so a new joiner doesn't
        // jarringly mid-scroll; the animation restarts from top.
        key={attackers.length}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          animation: `hpfs-leaderboard-scroll ${duration}s linear infinite`,
        }}
      >
        {attackers.map((p, i) => renderRow(p, i, 'a'))}
        {attackers.map((p, i) => renderRow(p, i, 'b'))}
      </div>
    </div>
  );
}

function AttackerRow({
  rank,
  participant,
  isLeader,
  accent,
  secondary,
}: {
  rank: number;
  participant: WildParticipant;
  isLeader: boolean;
  accent: string;
  secondary: string;
}) {
  const hasHp =
    typeof participant.currentHp === 'number' &&
    typeof participant.maxHp === 'number' &&
    participant.maxHp > 0;
  const hpPct = hasHp ? Math.max(0, Math.min(1, participant.currentHp! / participant.maxHp!)) : 0;
  const fainted = participant.isFainted === true || (hasHp && participant.currentHp! <= 0);
  const hpColor = fainted
    ? '#6b7280'
    : hpPct >= 0.5
      ? '#22c55e'
      : hpPct >= 0.2
        ? '#f59e0b'
        : '#ef4444';

  // 2026-06-20 — surface trap-catch outcomes prominently. participant
  // .caught flips true on a successful catch; catchAttempted true +
  // caught false means the trap fired and missed. Background tint +
  // big rank-column badge replace the standard #N so chatters spot
  // catch results across the room.
  const catchOutcome: 'success' | 'fail' | null = participant.caught
    ? 'success'
    : participant.catchAttempted
      ? 'fail'
      : null;
  const catchSuccessColor = '#22c55e';
  const catchFailColor = '#ef4444';

  const rowBg =
    catchOutcome === 'success'
      ? `linear-gradient(90deg, ${catchSuccessColor}33, transparent)`
      : catchOutcome === 'fail'
        ? `linear-gradient(90deg, ${catchFailColor}33, transparent)`
        : isLeader
          ? `${accent}11`
          : 'rgba(255,255,255,0.02)';
  const rowBorder =
    catchOutcome === 'success'
      ? `1px solid ${catchSuccessColor}aa`
      : catchOutcome === 'fail'
        ? `1px solid ${catchFailColor}88`
        : isLeader
          ? `1px solid ${accent}55`
          : '1px solid rgba(255,255,255,0.06)';

  // The rank cell becomes a CAUGHT / MISS badge on catch attempts.
  let rankCell: React.ReactNode;
  if (catchOutcome === 'success') {
    rankCell = (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          fontFamily: DISPLAY_FONT,
          fontSize: 16,
          lineHeight: 1,
          color: catchSuccessColor,
          textShadow: `0 0 12px ${catchSuccessColor}aa`,
          animation: 'hpfs-catch-pop 0.6s ease-out',
        }}
        title="Successful catch"
      >
        ✅
      </span>
    );
  } else if (catchOutcome === 'fail') {
    rankCell = (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: DISPLAY_FONT,
          fontSize: 16,
          lineHeight: 1,
          color: catchFailColor,
          textShadow: `0 0 12px ${catchFailColor}aa`,
          animation: 'hpfs-catch-pop 0.6s ease-out',
        }}
        title="Catch missed"
      >
        ❌
      </span>
    );
  } else {
    rankCell = (
      <span
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 13,
          fontWeight: 800,
          color: isLeader ? accent : 'rgba(255,255,255,0.5)',
        }}
      >
        #{rank}
      </span>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr auto',
        gridTemplateRows: 'auto auto',
        gridTemplateAreas: `
          "rank name dmg"
          "rank bar  hp"
        `,
        alignItems: 'center',
        columnGap: 10,
        rowGap: 2,
        padding: '6px 10px',
        borderRadius: 4,
        background: rowBg,
        border: rowBorder,
        opacity: fainted && !catchOutcome ? 0.7 : 1,
      }}
    >
      <div style={{ gridArea: 'rank', display: 'flex', justifyContent: 'center' }}>{rankCell}</div>
      <span
        style={{
          gridArea: 'name',
          fontSize: 14,
          fontWeight: 700,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {fainted && !catchOutcome ? '💀 ' : ''}
        {participant.viewer}
        {catchOutcome === 'success' && (
          <span
            style={{
              marginLeft: 8,
              padding: '1px 6px',
              borderRadius: 3,
              background: `${catchSuccessColor}33`,
              color: catchSuccessColor,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              verticalAlign: 'middle',
            }}
          >
            Caught
          </span>
        )}
        {catchOutcome === 'fail' && (
          <span
            style={{
              marginLeft: 8,
              padding: '1px 6px',
              borderRadius: 3,
              background: `${catchFailColor}33`,
              color: catchFailColor,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              verticalAlign: 'middle',
            }}
          >
            Missed
          </span>
        )}
      </span>
      <span
        style={{
          gridArea: 'dmg',
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 13,
          fontWeight: 700,
          color: isLeader ? accent : 'rgba(255,255,255,0.7)',
          whiteSpace: 'nowrap',
        }}
      >
        {participant.damageDealt} dmg
      </span>
      <div
        style={{
          gridArea: 'bar',
          height: 6,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.06)',
          overflow: 'hidden',
        }}
      >
        {hasHp && (
          <div
            style={{
              width: `${hpPct * 100}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${hpColor}, ${secondary})`,
              transition: 'width 400ms ease',
              boxShadow: `0 0 8px ${hpColor}66`,
            }}
          />
        )}
      </div>
      <span
        style={{
          gridArea: 'hp',
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          fontWeight: 700,
          color: hasHp ? hpColor : 'rgba(255,255,255,0.35)',
          whiteSpace: 'nowrap',
          minWidth: 64,
          textAlign: 'right',
        }}
      >
        {hasHp ? `${participant.currentHp} / ${participant.maxHp} HP` : '— HP'}
      </span>
    </div>
  );
}

function IdlePanel({
  title,
  subtitle,
  accent,
  pulse,
}: {
  title: string;
  subtitle: string;
  accent: string;
  pulse?: boolean;
}) {
  return (
    <div
      style={{
        height: '100%',
        borderRadius: 12,
        background: 'rgba(0,0,0,0.45)',
        border: `1px solid ${accent}55`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 18,
        textAlign: 'center',
        padding: 60,
      }}
    >
      <div
        style={{
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${accent}55, transparent 70%)`,
          display: 'grid',
          placeItems: 'center',
          animation: pulse ? 'hpfs-pulse 2.4s ease-in-out infinite' : undefined,
        }}
      >
        <span style={{ fontSize: 120 }}>🐾</span>
      </div>
      <div
        style={{
          fontFamily: DISPLAY_FONT,
          fontSize: 64,
          lineHeight: 0.9,
          letterSpacing: -1,
          color: 'white',
          textShadow: `0 0 40px ${accent}55`,
        }}
      >
        {title.toUpperCase()}
      </div>
      <div style={{ fontSize: 18, color: 'rgba(255,255,255,0.6)', maxWidth: 540 }}>{subtitle}</div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Who's That Pet? — between-fights mini-game.
 *
 * Fetches a random Petdex species from /api/hypeegg/random-species,
 * renders the species art as a heavily-blurred + desaturated teaser
 * with a pulsing "?" stamp on top (flux-painted sprites have opaque
 * backgrounds so a true brightness(0) silhouette just produces a
 * black square — blur preserves rough shape + color for chat to
 * guess from), and listens for chat-message socket pushes to find
 * the first viewer who guesses the species name correctly. Reveals
 * on correct OR after the 25s timer expires; auto-cycles to the next
 * round after a 6s reveal pause.
 *
 * Pure client-side game state — no engine plumbing. Multiple overlays
 * for the same streamer will desync (each picks its own species), but
 * most streamers run one overlay, and the trade-off is shipping fast
 * without new chat-ingest game-state machinery.
 * ────────────────────────────────────────────────────────────────── */

interface MinigameSpecies {
  id: string;
  name: string;
  imageUrl: string | null;
  emoji: string;
  rarity: string;
  theme: string | null;
}
type MinigamePhase = 'loading' | 'guessing' | 'revealed';
const MINIGAME_GUESS_MS = 25_000;
const MINIGAME_REVEAL_MS = 6_000;

/** Strip everything but a-z 0-9 so "Sasquatch", "sas-quatch", and
 *  "SASQUATCH!" all collapse to "sasquatch". */
function normalizeGuess(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function WhosThatPetPanel({
  accent,
  secondary,
  latestChatMessage,
}: {
  accent: string;
  secondary: string;
  latestChatMessage: ChatMessage | null;
}) {
  const [species, setSpecies] = useState<MinigameSpecies | null>(null);
  const [phase, setPhase] = useState<MinigamePhase>('loading');
  const [endsAt, setEndsAt] = useState<number>(0);
  const [winner, setWinner] = useState<string | null>(null);
  const [recentGuesses, setRecentGuesses] = useState<
    Array<{ viewer: string; guess: string; correct: boolean; ts: number }>
  >([]);
  const [_tick, setTick] = useState(0);
  const lastChatIdRef = useRef<string | null>(null);

  /** Fetch a new species + reset state. Used on mount + after each
   *  reveal completes. Survives transient network errors — failure
   *  shows the loading shell, next interval retries. */
  const loadRound = useCallback(async () => {
    setPhase('loading');
    setWinner(null);
    setRecentGuesses([]);
    setSpecies(null);
    try {
      const tokenMatch = window.location.search.match(/token=([^&]+)/);
      if (!tokenMatch) return;
      const res = await fetch(`/api/hypeegg/random-species?token=${tokenMatch[1]}`);
      if (!res.ok) return;
      const json = (await res.json()) as MinigameSpecies | null;
      if (!json) return;
      setSpecies(json);
      setEndsAt(Date.now() + MINIGAME_GUESS_MS);
      setPhase('guessing');
    } catch {
      // swallow — UI stays on loading shell, next mount/tick retries
    }
  }, []);

  // Bootstrap first round on mount.
  useEffect(() => {
    void loadRound();
  }, [loadRound]);

  // 1Hz tick to drive the countdown text + the auto-advance to reveal
  // / next round.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Phase transitions:
  //   guessing  → revealed (on timer expiry or correct guess)
  //   revealed  → loading  → guessing (auto-cycle after reveal pause)
  // 2026-06-21 — timer-expiry reveals play the softer 'reveal' cue
  // (the timeout case). Winning-guess reveals are triggered from the
  // chat handler below, which plays the punchier 'correct' cue.
  useEffect(() => {
    if (phase === 'guessing' && Date.now() >= endsAt) {
      void gameSounds.play('reveal');
      setPhase('revealed');
      setEndsAt(Date.now() + MINIGAME_REVEAL_MS);
    } else if (phase === 'revealed' && Date.now() >= endsAt) {
      void loadRound();
    }
  });

  // Listen for chat guesses. Each new chat-message push is checked
  // against the active species name. First normalized match wins.
  useEffect(() => {
    if (!latestChatMessage) return;
    if (lastChatIdRef.current === latestChatMessage.id) return;
    lastChatIdRef.current = latestChatMessage.id;
    if (phase !== 'guessing' || !species) return;
    if (latestChatMessage.command) return; // ignore !use, !attack, etc.
    const raw = (latestChatMessage.message ?? '').trim();
    if (!raw) return;
    const guessNorm = normalizeGuess(raw);
    if (!guessNorm) return;
    const targetNorm = normalizeGuess(species.name);
    const correct = guessNorm === targetNorm;
    setRecentGuesses((prev) =>
      [
        ...prev,
        {
          viewer: latestChatMessage.viewerName,
          guess: raw.slice(0, 32),
          correct,
          ts: Date.now(),
        },
      ].slice(-6),
    );
    if (correct && !winner) {
      // 2026-06-21 — punchy win chime on a correct guess; the softer
      // 'reveal' cue is reserved for the timer-expiry no-winner path.
      void gameSounds.play('correct');
      setWinner(latestChatMessage.viewerName);
      setPhase('revealed');
      setEndsAt(Date.now() + MINIGAME_REVEAL_MS);
    }
  }, [latestChatMessage, phase, species, winner]);

  const isRevealed = phase === 'revealed';
  const remainingSec = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        borderRadius: 12,
        background: 'rgba(0,0,0,0.45)',
        border: `1px solid ${accent}55`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        padding: 32,
        overflow: 'hidden',
        boxShadow: `0 24px 80px -20px ${accent}33, inset 0 0 80px ${accent}11`,
        display: 'grid',
        gridTemplateColumns: '320px minmax(0,1fr)',
        gap: 36,
      }}
    >
      {/* Silhouette / reveal column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 320,
            height: 320,
            borderRadius: 12,
            border: `2px solid ${isRevealed ? '#22c55e' : `${accent}aa`}`,
            background: 'rgba(0,0,0,0.5)',
            overflow: 'hidden',
            position: 'relative',
            display: 'grid',
            placeItems: 'center',
            boxShadow: isRevealed ? `0 0 60px rgba(34,197,94,0.55)` : `0 0 60px ${accent}55`,
            transition: 'box-shadow 600ms ease, border-color 600ms ease',
          }}
        >
          {species?.imageUrl ? (
            <img
              src={species.imageUrl}
              alt={isRevealed ? species.name : '???'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                // 2026-06-20 — heavy blur + slight desaturation teases
                // the species without needing a transparent silhouette
                // (flux-painted sprites carry opaque painted
                // backgrounds, so the classic CSS brightness(0)
                // silhouette just produced a black square). Chat
                // guesses from rough shape + dominant color. 600ms
                // ease-out on reveal is the "tada" moment.
                filter: isRevealed ? 'none' : 'blur(8px) saturate(0.75) brightness(0.92)',
                transition: 'filter 700ms cubic-bezier(0.22, 1, 0.36, 1)',
                transform: isRevealed ? 'scale(1)' : 'scale(1.08)',
              }}
            />
          ) : (
            <span
              style={{
                fontSize: 200,
                lineHeight: 1,
                filter: isRevealed ? 'none' : 'blur(6px) saturate(0.75)',
                transition: 'filter 700ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              {species?.emoji ?? '🐾'}
            </span>
          )}
          {/* Big "?" stamp during the guess phase — purely cosmetic
              flag so chatters joining mid-round know it's a guessing
              round, not a wild encounter rendering at low quality. */}
          {!isRevealed && phase !== 'loading' && (
            <span
              style={{
                position: 'absolute',
                fontFamily: DISPLAY_FONT,
                fontSize: 220,
                lineHeight: 1,
                color: 'rgba(255,255,255,0.85)',
                textShadow: `0 0 28px ${accent}, 0 8px 32px rgba(0,0,0,0.7)`,
                pointerEvents: 'none',
                animation: 'hpfs-pulse 2.4s ease-in-out infinite',
              }}
            >
              ?
            </span>
          )}
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            borderRadius: 4,
            background: isRevealed ? 'rgba(34,197,94,0.22)' : 'rgba(255,255,255,0.06)',
            color: isRevealed ? '#86efac' : 'rgba(255,255,255,0.65)',
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 4,
            textTransform: 'uppercase',
          }}
        >
          {isRevealed ? (species?.rarity ?? 'revealed') : '???'}
        </span>
      </div>

      {/* Details column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '4px 12px',
              borderRadius: 4,
              background: `${secondary}22`,
              color: secondary,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 3,
              textTransform: 'uppercase',
            }}
          >
            Mini-Game
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 14,
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            {phase === 'loading'
              ? 'rolling…'
              : isRevealed
                ? `next in ${remainingSec}s`
                : `${remainingSec}s left`}
          </span>
        </div>
        <div
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: 84,
            lineHeight: 0.9,
            letterSpacing: -1,
            color: 'white',
            textShadow: `0 0 60px ${accent}55`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isRevealed && species ? `IT'S ${species.name.toUpperCase()}!` : "WHO'S THAT HYPEPET?"}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {isRevealed ? (
            winner ? (
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: '#86efac',
                  textShadow: '0 0 24px rgba(34,197,94,0.6)',
                }}
              >
                🏆 {winner} got it!
              </span>
            ) : (
              <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>
                No one guessed it — better luck next round.
              </span>
            )
          ) : (
            <span style={{ fontSize: 20, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
              Type the pet's name in chat to win!
            </span>
          )}
        </div>

        {/* Recent-guesses ticker — caps at 6 entries, newest first. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 4,
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
            }}
          >
            Recent Guesses
          </span>
          {recentGuesses.length === 0 ? (
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
              Waiting for chat to take a shot…
            </span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[...recentGuesses].reverse().map((g, idx) => (
                <div
                  key={`${g.ts}-${idx}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '20px 1fr auto',
                    alignItems: 'center',
                    gap: 10,
                    padding: '4px 10px',
                    borderRadius: 4,
                    background: g.correct ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.02)',
                    border: g.correct
                      ? '1px solid rgba(34,197,94,0.55)'
                      : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span style={{ fontSize: 14 }}>{g.correct ? '✅' : '💭'}</span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontWeight: 800, color: g.correct ? '#86efac' : accent }}>
                      {g.viewer}
                    </span>
                    <span style={{ marginLeft: 6, color: 'rgba(255,255,255,0.65)' }}>
                      "{g.guess}"
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LeaderboardPanel({
  metric,
  rows,
  accent,
  rotating,
}: {
  metric: MetricDef;
  rows: LeaderboardEntry[] | null;
  accent: string;
  rotating: boolean;
}) {
  // Keep DOM stable across metric swaps — keying on metric.id animates
  // the swap so chatters notice the rotation without us doing anything
  // explicit.
  return (
    <div
      style={{
        height: '100%',
        borderRadius: 12,
        background: 'rgba(0,0,0,0.45)',
        border: `1px solid ${accent}55`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        padding: 28,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        boxShadow: `0 24px 80px -20px ${accent}33, inset 0 0 80px ${accent}11`,
      }}
    >
      <div key={metric.id} style={{ animation: 'hpfs-pulse 0.6s ease-out' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span
              style={{
                fontFamily: DISPLAY_FONT,
                fontSize: 44,
                lineHeight: 0.9,
                letterSpacing: -0.5,
                color: 'white',
                textShadow: `0 0 30px ${accent}55`,
              }}
            >
              {metric.icon} {metric.title.toUpperCase()}
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 4,
                color: `${accent}cc`,
                textTransform: 'uppercase',
              }}
            >
              {metric.subtitle}
              {rotating && <span style={{ opacity: 0.5 }}> · rotating</span>}
            </span>
          </div>
        </div>
      </div>
      {!rows ? (
        <div
          style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            color: 'rgba(255,255,255,0.4)',
            fontSize: 14,
          }}
        >
          Loading leaderboard…
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            color: 'rgba(255,255,255,0.4)',
            fontSize: 14,
            fontStyle: 'italic',
            textAlign: 'center',
            padding: 20,
          }}
        >
          No one's on the board for {metric.subtitle.toLowerCase()} yet.
          <br />
          First chatter to score gets the top spot.
        </div>
      ) : (
        <ScrollingLeaderboard rows={rows} metric={metric} accent={accent} />
      )}
    </div>
  );
}

/** 2026-06-20 — slow vertical marquee for the leaderboard rows.
 *  Renders the row list twice back-to-back, animates translateY from
 *  0 → -50% linearly so the seam between copies is invisible. Pauses
 *  (no animation, static list) when the list is short enough to fit
 *  the visible window (≤5 rows is the heuristic). Resets on every
 *  metric swap so each cycle starts from #1. */
function ScrollingLeaderboard({
  rows,
  metric,
  accent,
}: {
  rows: LeaderboardEntry[];
  metric: MetricDef;
  accent: string;
}) {
  const SECONDS_PER_ROW = 3.5; // dwell-per-row when scrolling
  const VISIBLE_THRESHOLD = 5; // skip animation when the list fits
  const shouldScroll = rows.length > VISIBLE_THRESHOLD;
  const duration = Math.max(20, Math.round(rows.length * SECONDS_PER_ROW));

  const renderRow = (r: LeaderboardEntry, i: number, keyPrefix: string) => {
    const value = metric.read(r);
    const isLeader = i === 0;
    return (
      <div
        key={`${keyPrefix}:${r.platform}:${r.platformUsername}`}
        style={{
          display: 'grid',
          gridTemplateColumns: '36px 1fr auto',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          borderRadius: 6,
          background: isLeader
            ? `linear-gradient(90deg, ${accent}33, transparent)`
            : 'rgba(255,255,255,0.03)',
          border: isLeader ? `1px solid ${accent}aa` : '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: 28,
            lineHeight: 1,
            color: isLeader ? accent : 'rgba(255,255,255,0.5)',
            textShadow: isLeader ? `0 0 12px ${accent}aa` : undefined,
          }}
        >
          {isLeader ? '👑' : `#${i + 1}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>{PLATFORM_GLYPH[r.platform]}</span>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {r.platformUsername}
          </span>
        </div>
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 18,
            fontWeight: 800,
            color: isLeader ? accent : 'white',
            flexShrink: 0,
          }}
        >
          {value.toLocaleString()}
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              marginLeft: 4,
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            {metric.unit}
          </span>
        </span>
      </div>
    );
  };

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        position: 'relative',
        overflow: 'hidden',
        // Top + bottom fade so rows enter/exit softly instead of
        // popping at the container edge.
        maskImage:
          'linear-gradient(180deg, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(180deg, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
      }}
    >
      <div
        // Key on metric.id so each metric swap restarts the scroll
        // animation from the top.
        key={metric.id}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          animation: shouldScroll
            ? `hpfs-leaderboard-scroll ${duration}s linear infinite`
            : undefined,
        }}
      >
        {rows.map((r, i) => renderRow(r, i, 'a'))}
        {/* Second copy so the loop seam is invisible (translateY -50%
            lands exactly on the second copy's start, which equals the
            first copy's start visually). Skip when not scrolling so
            chatters don't see a duplicated short list. */}
        {shouldScroll && rows.map((r, i) => renderRow(r, i, 'b'))}
      </div>
    </div>
  );
}
